import { z } from 'zod';

/**
 * The `period` request-key shapes the workspace usage endpoint accepts for the non-Monthly
 * period types (spec 093). Monthly's shapes (`undefined` or `YYYY-MM`) are already produced by
 * `PeriodStepper`; these two cover Daily and Weekly.
 *
 * Computed in UTC and formatted directly to a string, matching `periodCopy.ts`'s
 * `nextBoundaryUtc` — a `Date` built from a date-only string is UTC midnight, and month/day
 * arithmetic on it in a westward local timezone can land a day early, so this never routes
 * through a locally-interpreted `Date`.
 */

/** Today, as `daily:YYYY-MM-DD`. `now` is injectable for tests; defaults to the real clock. */
export function todayKey(now: Date = new Date()): string {
  return `daily:${utcDateString(now)}`;
}

/**
 * The Monday of the current UTC week, as `weekly:YYYY-MM-DD`. `now` is injectable for tests;
 * defaults to the real clock.
 */
export function thisWeekMondayKey(now: Date = new Date()): string {
  const monday = new Date(now);
  monday.setUTCHours(0, 0, 0, 0);
  // getUTCDay() is 0=Sun..6=Sat; ISO weekday treats Sunday as 7, so days since Monday is
  // isoWeekday - 1, matching `periodCopy.ts`'s isoWeekday convention for the same week.
  const isoWeekday = monday.getUTCDay() === 0 ? 7 : monday.getUTCDay();
  monday.setUTCDate(monday.getUTCDate() - (isoWeekday - 1));
  return `weekly:${utcDateString(monday)}`;
}

/**
 * The current UTC calendar month, as `YYYY-MM`. Used by `PeriodStepper` when the
 * currently-displayed period isn't Monthly, so its month arithmetic always has a real
 * calendar month to step from. `now` is injectable for tests; defaults to the real clock.
 */
export function currentMonthKey(now: Date = new Date()): string {
  const year = String(now.getUTCFullYear()).padStart(4, '0');
  const month = String(now.getUTCMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
}

function utcDateString(date: Date): string {
  const year = String(date.getUTCFullYear()).padStart(4, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * The `?period=` search parameter of every page that reads a period (spec 072 FR-005):
 * Usage, and the AI models page (spec 215), so a link that works on one works on the
 * other. Validated rather than trusted: a malformed value falls back to the current
 * period instead of reaching the service and coming back a 422, which would show an
 * error page for what is really a bad bookmark.
 */
export const periodSearchSchema = z.object({
  period: z
    .string()
    .regex(
      /^(\d{4}-(0[1-9]|1[0-2])|daily:\d{4}-\d{2}-\d{2}|weekly:\d{4}-\d{2}-\d{2}|none)$/,
    )
    .optional()
    .catch(undefined),
});

/**
 * The Spend tab's address (spec 254): the period, and which Over time view is open,
 * so a link opens on the view it was copied from. Cumulative is the default and is
 * left out of the address.
 */
export const spendSearchSchema = periodSearchSchema.extend({
  view: z.enum(['cumulative', 'per-day']).optional().catch(undefined),
  /** What the Per day bars split by (spec 257); workflow, the default, is left out. */
  split: z
    .enum(['workflow', 'person', 'profile', 'model'])
    .optional()
    .catch(undefined),
});
